import { useEffect, useMemo, useState } from "react";

import { AnimatePresence } from "framer-motion";
import { sortBy } from "lodash";
import moment from "moment";

import { useSalesBreakdownConfigs } from "@components/data/sales-breakdown-configs";
import { BreakdownSelector } from "@components/reports/breakdown-selector";
import { useUser } from "@components/user-context";
import { TertiaryButton } from "@components/v2";
import { usePrettyLogEventPage } from "@components/v2/analytics";
import {
  createDataExportMetric,
  createInitialMetrics,
} from "@features/data-export/utils";
import { type SalesPageMetricSpec } from "@north-beam/nb-client-side";
import {
  type Breakdown,
  NBAccountingMode,
  type IGranularity,
  type IMCAttributionWindowDays,
  type INBAccountingMode,
  type ITimeGranularityV2,
} from "@north-beam/nb-common";
import { useAttributionWindowOptions } from "@pages/sales/report-body-control/attribution-window-select";
import { TextInput } from "@shared/text-input";

import {
  type TimeFrame,
  type DataExportMetric,
  type DataExportTarget,
  type SalesMetricsConfig,
  type SalesMetricsExportAggregation,
} from "../../types";

import { Accordion } from "./accordion";
import { AccordionWithCheckboxes } from "./accordion-with-checkboxes";
import { AccordionWithRadios } from "./accordion-with-radios";
import { ExtraOptionsSection } from "./extra-options-section";
import { MetricEditor } from "./metric-editor";
import { SelectedMetrics } from "./selected-metrics";
import { TimeFrameSelector } from "./time-frame-selector";

interface LevelOption {
  id: IGranularity;
  label: string;
}

const PERFORMANCE_METRICS_LEVEL_OPTIONS: LevelOption[] = [
  { id: "platform", label: "Platform" },
  { id: "campaign", label: "Campaign" },
  { id: "adset", label: "Adset" },
  { id: "ad", label: "Ad" },
];

const LEVEL_OPTIONS_BY_TARGET: Record<DataExportTarget, LevelOption[]> = {
  "performance-metrics": PERFORMANCE_METRICS_LEVEL_OPTIONS,
} as const;

interface Props {
  target: DataExportTarget;
  availableMetricSpecs: SalesPageMetricSpec[];
  selectedMetricIds: string[];
  attributionModel: string;
  defaultAttributionModels: string[];
  attributionWindow: IMCAttributionWindowDays;
  defaultAttributionWindows: IMCAttributionWindowDays[];
  nbAccountingMode: INBAccountingMode;
  defaultAccountingModes: INBAccountingMode[];
  granularity: IGranularity;
  timeGranularity: ITimeGranularityV2;
  breakdowns: Breakdown[];
  dateRange: TimeFrame;
  initialExportAggregation: SalesMetricsExportAggregation;
  initialRemoveZeroSpend: boolean;
  initialAggregateData: boolean;
  initialIncludeIds: boolean;
  initialIncludeKindAndPlatform: boolean;
  fileName: string;
  onChange(config: SalesMetricsConfig): void;
}

const METRIC_CATEGORY_DEFAULTS = {
  "northbeam-metrics": true,
  "custom-metrics": false,
  "subscription-metrics": false,
  "facebook-metrics": false,
  "tiktok-metrics": false,
  "google-metrics": false,
  "pinterest-metrics": false,
  "snapchat-metrics": false,
  "twitter-metrics": false,
  "tradedesk-metrics": false,
};

export function MetricsConfigurator({
  target,
  availableMetricSpecs,
  selectedMetricIds,
  attributionModel,
  defaultAttributionModels = [],
  attributionWindow: selectedAttributionWindow,
  defaultAttributionWindows = [],
  nbAccountingMode,
  defaultAccountingModes = [],
  granularity,
  timeGranularity,
  breakdowns,
  dateRange,
  initialExportAggregation,
  initialRemoveZeroSpend,
  initialAggregateData,
  initialIncludeIds,
  initialIncludeKindAndPlatform,
  fileName,
  onChange,
}: Props) {
  const prettyLogEventPage = usePrettyLogEventPage();

  const { user } = useUser();
  const { value: breakdownConfigs } = useSalesBreakdownConfigs();

  const { options: windowOptions, reconciledAttributionWindow } =
    useAttributionWindowOptions({
      timeGranularity: "daily",
      selectedAttributionWindow,
    });

  const initialAttributionModelIds = [
    attributionModel,
    ...defaultAttributionModels,
  ];
  const initialAttributionWindowIds = [
    reconciledAttributionWindow,
    ...defaultAttributionWindows,
  ];
  const initialAccountingModeIds = [
    nbAccountingMode,
    ...defaultAccountingModes,
  ];

  const [timeFrame, setTimeFrame] = useState<TimeFrame>(
    typeof dateRange === "string"
      ? dateRange
      : {
          type: "fixed",
          startDate: moment(dateRange.startDate).format("YYYY-MM-DD"),
          endDate: moment(dateRange.endDate).format("YYYY-MM-DD"),
        },
  );

  const [selectedMetrics, setSelectedMetrics] = useState<DataExportMetric[]>(
    () =>
      createInitialMetrics(
        user.attributionMethodEnum.reactSelectOptions.map((e) => e.value),
        availableMetricSpecs,
        selectedMetricIds,
        initialAttributionModelIds,
        initialAttributionWindowIds,
        initialAccountingModeIds,
      ),
  );

  const [editedMetricId, setEditedMetricId] = useState<string | null>(null);
  const [selectedAttributionModelIds, setSelectedAttributionModelIds] =
    useState(() => new Set<string>(initialAttributionModelIds));
  const [selectedAttributionWindowIds, setSelectedAttributionWindowIds] =
    useState(
      () => new Set<IMCAttributionWindowDays>(initialAttributionWindowIds),
    );

  const [selectedAccountingModeIds, setSelectedAccountingModeIds] = useState(
    () => new Set<INBAccountingMode>(initialAccountingModeIds),
  );
  const [exportAggregation, setExportAggregation] =
    useState<SalesMetricsExportAggregation>(initialExportAggregation);
  const [removeZeroSpend, setRemoveZeroSpend] = useState(
    initialRemoveZeroSpend,
  );
  const [aggregateData, setAggregateData] = useState(initialAggregateData);
  const [includeIds, setIncludeIds] = useState(initialIncludeIds);
  const [includeKindAndPlatform, setIncludeKindAndPlatform] = useState(
    initialIncludeKindAndPlatform,
  );
  const [selectedLevel, setSelectedLevel] = useState(granularity);
  const [selectedBreakdowns, setSelectedBreakdowns] = useState<Breakdown[]>(
    () => breakdowns,
  );
  const [selectedTimeGranularity, setSelectedTimeGranularity] =
    useState<ITimeGranularityV2>(timeGranularity);

  const [selectedFileName, setSelectedFileName] = useState<string>(fileName);

  useEffect(() => {
    const defaultAttributionModels =
      user.attributionMethodEnum.reactSelectOptions
        .filter((e) => selectedAttributionModelIds.has(e.value))
        .map((e) => e.value);

    const defaultAttributionWindows = windowOptions
      .filter((e) => selectedAttributionWindowIds.has(e.value))
      .map((e) => e.value);

    const defaultAccountingModes = NBAccountingMode.reactSelectOptions
      .filter((e) => selectedAccountingModeIds.has(e.value))
      .map((e) => e.value);

    onChange({
      metrics: selectedMetrics,
      exportAggregation,
      removeZeroSpend,
      aggregateData,
      includeIds,
      includeKindAndPlatform,
      level: selectedLevel,
      breakdowns: selectedBreakdowns,
      defaultAttributionModels,
      defaultAttributionWindows,
      defaultAccountingModes,
      timeFrame,
      timeGranularity: selectedTimeGranularity,
      fileName: selectedFileName,
    });
  }, [
    onChange,
    user,
    selectedMetrics,
    exportAggregation,
    removeZeroSpend,
    aggregateData,
    includeIds,
    includeKindAndPlatform,
    selectedLevel,
    selectedBreakdowns,
    selectedAttributionModelIds,
    selectedAttributionWindowIds,
    selectedAccountingModeIds,
    timeFrame,
    selectedTimeGranularity,
    selectedFileName,
    windowOptions,
  ]);

  const sortedSalesPageMetricSpecs = useMemo(
    () => sortBy(availableMetricSpecs, (e) => e.name.toLowerCase()),
    [availableMetricSpecs],
  );

  const defaultSalesPageMetricSpecs = useMemo(
    () => sortedSalesPageMetricSpecs.filter((e) => e.group === "default"),
    [sortedSalesPageMetricSpecs],
  );

  const customSalesPageMetricSpecs = useMemo(
    () => sortedSalesPageMetricSpecs.filter((e) => e.group === "custom"),
    [sortedSalesPageMetricSpecs],
  );

  const subscriptionsSalesPageMetricSpecs = useMemo(
    () => sortedSalesPageMetricSpecs.filter((e) => e.group === "subscriptions"),
    [sortedSalesPageMetricSpecs],
  );

  const metaSalesPageMetricSpecs = useMemo(
    () => sortedSalesPageMetricSpecs.filter((e) => e.group === "meta"),
    [sortedSalesPageMetricSpecs],
  );

  const tiktokSalesPageMetricSpecs = useMemo(
    () => sortedSalesPageMetricSpecs.filter((e) => e.group === "tiktok"),
    [sortedSalesPageMetricSpecs],
  );

  const googleSalesPageMetricSpecs = useMemo(
    () => sortedSalesPageMetricSpecs.filter((e) => e.group === "google"),
    [sortedSalesPageMetricSpecs],
  );

  const pinterestSalesPageMetricSpecs = useMemo(
    () => sortedSalesPageMetricSpecs.filter((e) => e.group === "pinterest"),
    [sortedSalesPageMetricSpecs],
  );

  const snapchatSalesPageMetricSpecs = useMemo(
    () => sortedSalesPageMetricSpecs.filter((e) => e.group === "snapchat"),
    [sortedSalesPageMetricSpecs],
  );

  const twitterSalesPageMetricSpecs = useMemo(
    () => sortedSalesPageMetricSpecs.filter((e) => e.group === "twitter"),
    [sortedSalesPageMetricSpecs],
  );

  const tradedeskSalesPageMetricSpecs = useMemo(
    () => sortedSalesPageMetricSpecs.filter((e) => e.group === "tradedesk"),
    [sortedSalesPageMetricSpecs],
  );

  const [metricNameSearch, setMetricNameSearch] = useState("");

  // Expand states for each accordion section
  const [expandedSections, setExpandedSections] = useState<
    typeof METRIC_CATEGORY_DEFAULTS
  >(METRIC_CATEGORY_DEFAULTS);

  const setExpandedSection = (sectionId: string, expanded: boolean) => {
    setExpandedSections((prev) => ({ ...prev, [sectionId]: expanded }));
  };

  const expandAll = () => {
    setExpandedSections(
      Object.keys(METRIC_CATEGORY_DEFAULTS).reduce(
        (acc, key) => ({ ...acc, [key]: true }),
        {} as typeof METRIC_CATEGORY_DEFAULTS,
      ),
    );
  };

  const collapseAll = () => {
    setExpandedSections(
      Object.keys(METRIC_CATEGORY_DEFAULTS).reduce(
        (acc, key) => ({ ...acc, [key]: false }),
        {} as typeof METRIC_CATEGORY_DEFAULTS,
      ),
    );
  };

  const editedMetric = useMemo(
    () => selectedMetrics.find(({ id }) => id === editedMetricId),
    [selectedMetrics, editedMetricId],
  );

  const levelOptions = useMemo(() => {
    return LEVEL_OPTIONS_BY_TARGET[target];
  }, [target]);

  const updateSelectedMetricsWithAttributionModelIds = (
    newSelectedAttributionModelIds: Set<string>,
  ) => {
    const newSelectedMetrics = selectedMetrics.map((metric) => {
      const metricSpec = availableMetricSpecs.find((e) => e.id === metric.id)!;
      const specAttributionModel = metricSpec.modifiers.attributionModel;

      if (specAttributionModel === "all") {
        const attributionModels = new Set<string>(
          newSelectedAttributionModelIds,
        );

        if (attributionModels.size === 0) {
          attributionModels.add(
            user.attributionMethodEnum.reactSelectOptions[0].value,
          );
        }

        return { ...metric, attributionModels };
      } else {
        return metric;
      }
    });

    setSelectedMetrics(newSelectedMetrics);
  };

  const updateSelectedMetricsWithAttributionWindowIds = (
    selectedAttributionWindowIds: Set<IMCAttributionWindowDays>,
  ) => {
    const newSelectedMetrics = selectedMetrics.map((metric) => {
      const metricSpec = availableMetricSpecs.find((e) => e.id === metric.id)!;
      const specAttributionWindow = metricSpec.modifiers.attributionWindows;

      if (specAttributionWindow.length > 1) {
        const intersection = new Set(
          [...specAttributionWindow].filter((e) =>
            selectedAttributionWindowIds.has(e),
          ),
        );

        if (intersection.size === 0) {
          intersection.add(specAttributionWindow[0]);
        }

        return { ...metric, attributionWindows: intersection };
      } else {
        return metric;
      }
    });

    setSelectedMetrics(newSelectedMetrics);
  };

  const updateSelectedMetricsWithAccountingModeIds = (
    selectedAccountingModeIds: Set<INBAccountingMode>,
  ) => {
    const newSelectedMetrics = selectedMetrics.map((metric) => {
      const metricSpec = availableMetricSpecs.find((e) => e.id === metric.id)!;
      const specAccountingModes = metricSpec.modifiers.accountingModes;

      if (specAccountingModes.length > 1) {
        const intersection = new Set(
          [...specAccountingModes].filter((e) =>
            selectedAccountingModeIds.has(e),
          ),
        );

        if (intersection.size === 0) {
          intersection.add(specAccountingModes[0]);
        }

        return { ...metric, accountingModes: intersection };
      } else {
        return metric;
      }
    });

    setSelectedMetrics(newSelectedMetrics);
  };

  const updateAccountingModes = (metrics: DataExportMetric[]) => {
    const hasAccrualOnly = metrics.some((metric) => {
      const metricSpec = availableMetricSpecs.find((e) => e.id === metric.id)!;
      const accountingModes = metricSpec.modifiers.accountingModes;
      return accountingModes.length === 1 && accountingModes[0] === "accrual";
    });

    const hasCashOnly = metrics.some((metric) => {
      const metricSpec = availableMetricSpecs.find((e) => e.id === metric.id)!;
      const accountingModes = metricSpec.modifiers.accountingModes;
      return accountingModes.length === 1 && accountingModes[0] === "cash";
    });

    if (hasAccrualOnly || hasCashOnly) {
      const newVals = new Set(selectedAccountingModeIds);
      if (hasAccrualOnly) {
        newVals.add("accrual");
      }
      if (hasCashOnly) {
        newVals.add("cash");
      }

      setSelectedAccountingModeIds(newVals);
    }
  };

  const unselectMetricsForUnselectedAccountingModes = (
    selectedAccountingModeIds: Set<INBAccountingMode>,
  ) => {
    const allAccountingModes = NBAccountingMode.reactSelectOptions.map(
      (e) => e.value,
    );
    const unselectedAccountingModes = allAccountingModes.filter(
      (e) => !selectedAccountingModeIds.has(e),
    );

    const newSelectedMetrics = selectedMetrics.filter((metric) => {
      const metricSpec = availableMetricSpecs.find((e) => e.id === metric.id)!;
      const specAccountingModes = metricSpec.modifiers.accountingModes;

      return !(
        specAccountingModes.length === 1 &&
        unselectedAccountingModes.includes(specAccountingModes[0])
      );
    });

    if (newSelectedMetrics.length === 0) {
      newSelectedMetrics.push(
        createDataExportMetric(
          user.attributionMethodEnum.reactSelectOptions.map((e) => e.value),
          sortedSalesPageMetricSpecs[0],
          selectedAttributionModelIds,
          selectedAttributionWindowIds,
          selectedAccountingModeIds,
        ),
      );
    }

    setSelectedMetrics(newSelectedMetrics);
  };

  const buildOptionsForMetricSection = (
    groupSalesPageMetricSpecs: SalesPageMetricSpec[],
  ) =>
    groupSalesPageMetricSpecs.map(({ id, name }) => {
      const isSelected = selectedMetrics.some((e) => e.id === id);

      return {
        id: id,
        label: name,
        isSelected,
        isDisabled: false,
      };
    });

  const changeSingleMetric = (val: boolean, id: string) => {
    prettyLogEventPage({
      domain: "DataExport",
      action: "onSelect",
      additionalActionInfo: "Metric changed",
      data: {
        pageContext: { id },
      },
    });

    if (val) {
      const metricSpec = availableMetricSpecs.find((e) => e.id === id)!;

      const newMetrics = [
        ...selectedMetrics,
        createDataExportMetric(
          user.attributionMethodEnum.reactSelectOptions.map((e) => e.value),
          metricSpec,
          selectedAttributionModelIds,
          selectedAttributionWindowIds,
          selectedAccountingModeIds,
        ),
      ];

      setSelectedMetrics(newMetrics);
      updateAccountingModes(newMetrics);
    } else {
      setSelectedMetrics(selectedMetrics.filter((e) => e.id !== id));
    }
  };

  const changeAllMetrics = (
    val: boolean,
    groupSalesPageMetricSpecs: SalesPageMetricSpec[],
  ) => {
    prettyLogEventPage({
      domain: "DataExport",
      action: "onSelect",
      additionalActionInfo: "Metric all changed",
    });

    const createDefaultMetricSpec = (metricSpec: SalesPageMetricSpec) =>
      createDataExportMetric(
        user.attributionMethodEnum.reactSelectOptions.map((e) => e.value),
        metricSpec,
        selectedAttributionModelIds,
        selectedAttributionWindowIds,
        selectedAccountingModeIds,
      );

    if (val) {
      const newMetrics = [...selectedMetrics];

      for (const metricSpec of groupSalesPageMetricSpecs) {
        if (!newMetrics.some((e) => e.id === metricSpec.id)) {
          newMetrics.push(createDefaultMetricSpec(metricSpec));
        }
      }

      setSelectedMetrics(newMetrics);
      updateAccountingModes(newMetrics);
    } else {
      const groupMetricSpecIds = groupSalesPageMetricSpecs.map((e) => e.id);

      setSelectedMetrics((prevVal) => {
        const newVal = prevVal.filter(
          (e) => !groupMetricSpecIds.includes(e.id),
        );

        return newVal;
      });
    }
  };

  return (
    <div className="h-full grid grid-cols-[14rem_auto_18rem] overflow-hidden relative">
      <AnimatePresence>
        {editedMetric && (
          <MetricEditor
            metricSpec={
              availableMetricSpecs.find((e) => e.id === editedMetric.id)!
            }
            metric={editedMetric}
            onUpdateMetric={(metric) => {
              prettyLogEventPage({
                domain: "DataExport",
                action: "Other",
                additionalActionInfo: "Metric updated",
                data: {
                  pageContext: { id: metric.id },
                },
              });

              setSelectedMetrics(
                selectedMetrics.map((e) => {
                  if (e.id === metric.id) {
                    return { ...metric };
                  } else {
                    return e;
                  }
                }),
              );
            }}
            onClose={() => {
              prettyLogEventPage({
                domain: "DataExport",
                action: "onClose",
                additionalActionInfo: "Edit metric panel closed",
              });

              setEditedMetricId(null);
            }}
          />
        )}
      </AnimatePresence>
      <TimeFrameSelector
        target={target}
        timeGranularity={selectedTimeGranularity}
        timeFrame={timeFrame}
        fileName={selectedFileName}
        onTimeGranularityChange={(value) => setSelectedTimeGranularity(value)}
        onTimeFrameChange={(value) => setTimeFrame(value)}
        onFileNameChange={(value) => setSelectedFileName(value)}
      />
      <div className="py-5 overflow-y-scroll">
        <div className="border py-5 mx-5 rounded-lg">
          <div className="mx-8 text-xl font-semibold text-primary flex  justify-between">
            <div>Metrics</div>
            <div className="flex gap-1">
              <TertiaryButton onClick={collapseAll}>
                Collapse All
              </TertiaryButton>
              <TertiaryButton onClick={expandAll}>Expand All</TertiaryButton>
            </div>
          </div>
          <div className="mx-8 mt-5">
            <TextInput
              className=" w-full"
              value={metricNameSearch}
              placeholder="Search metrics"
              onChange={(e) => setMetricNameSearch(e.target.value)}
            />
          </div>
          <AccordionWithCheckboxes<string>
            title="Northbeam metrics"
            options={buildOptionsForMetricSection(defaultSalesPageMetricSpecs)}
            isExpandedByDefault={true}
            controlProps={{
              expanded: expandedSections["northbeam-metrics"] ?? false,
              setExpanded: (expanded) =>
                setExpandedSection("northbeam-metrics", expanded),
            }}
            onChange={(id, val) => changeSingleMetric(val, id)}
            onChangeAll={(val) =>
              changeAllMetrics(val, defaultSalesPageMetricSpecs)
            }
            search={metricNameSearch}
          />
          {customSalesPageMetricSpecs.length > 0 && (
            <AccordionWithCheckboxes<string>
              title="Custom metrics"
              isExpandedByDefault={false}
              controlProps={{
                expanded: expandedSections["custom-metrics"] ?? false,
                setExpanded: (expanded) =>
                  setExpandedSection("custom-metrics", expanded),
              }}
              options={buildOptionsForMetricSection(customSalesPageMetricSpecs)}
              onChange={(id, val) => changeSingleMetric(val, id)}
              onChangeAll={(val) =>
                changeAllMetrics(val, customSalesPageMetricSpecs)
              }
              search={metricNameSearch}
            />
          )}
          {subscriptionsSalesPageMetricSpecs.length > 0 && (
            <AccordionWithCheckboxes<string>
              title="Subscription metrics"
              isExpandedByDefault={false}
              controlProps={{
                expanded: expandedSections["subscription-metrics"] ?? false,
                setExpanded: (expanded) =>
                  setExpandedSection("subscription-metrics", expanded),
              }}
              options={buildOptionsForMetricSection(
                subscriptionsSalesPageMetricSpecs,
              )}
              search={metricNameSearch}
              onChange={(id, val) => changeSingleMetric(val, id)}
              onChangeAll={(val) =>
                changeAllMetrics(val, subscriptionsSalesPageMetricSpecs)
              }
            />
          )}
          {metaSalesPageMetricSpecs.length > 0 && (
            <AccordionWithCheckboxes<string>
              title="Facebook reported metrics"
              isExpandedByDefault={false}
              controlProps={{
                expanded: expandedSections["facebook-metrics"] ?? false,
                setExpanded: (expanded) =>
                  setExpandedSection("facebook-metrics", expanded),
              }}
              options={buildOptionsForMetricSection(metaSalesPageMetricSpecs)}
              onChange={(id, val) => changeSingleMetric(val, id)}
              search={metricNameSearch}
              onChangeAll={(val) =>
                changeAllMetrics(val, metaSalesPageMetricSpecs)
              }
            />
          )}
          {tiktokSalesPageMetricSpecs.length > 0 && (
            <AccordionWithCheckboxes<string>
              title="TikTok reported metrics"
              isExpandedByDefault={false}
              controlProps={{
                expanded: expandedSections["tiktok-metrics"],
                setExpanded: (expanded) =>
                  setExpandedSection("tiktok-metrics", expanded),
              }}
              options={buildOptionsForMetricSection(tiktokSalesPageMetricSpecs)}
              search={metricNameSearch}
              onChange={(id, val) => changeSingleMetric(val, id)}
              onChangeAll={(val) =>
                changeAllMetrics(val, tiktokSalesPageMetricSpecs)
              }
            />
          )}
          {googleSalesPageMetricSpecs.length > 0 && (
            <AccordionWithCheckboxes<string>
              title="Google reported metrics"
              isExpandedByDefault={false}
              controlProps={{
                expanded: expandedSections["google-metrics"],
                setExpanded: (expanded) =>
                  setExpandedSection("google-metrics", expanded),
              }}
              search={metricNameSearch}
              options={buildOptionsForMetricSection(googleSalesPageMetricSpecs)}
              onChange={(id, val) => changeSingleMetric(val, id)}
              onChangeAll={(val) =>
                changeAllMetrics(val, googleSalesPageMetricSpecs)
              }
            />
          )}
          {pinterestSalesPageMetricSpecs.length > 0 && (
            <AccordionWithCheckboxes<string>
              title="Pinterest reported metrics"
              isExpandedByDefault={false}
              controlProps={{
                expanded: expandedSections["pinterest-metrics"],
                setExpanded: (expanded) =>
                  setExpandedSection("pinterest-metrics", expanded),
              }}
              search={metricNameSearch}
              options={buildOptionsForMetricSection(
                pinterestSalesPageMetricSpecs,
              )}
              onChange={(id, val) => changeSingleMetric(val, id)}
              onChangeAll={(val) =>
                changeAllMetrics(val, pinterestSalesPageMetricSpecs)
              }
            />
          )}
          {snapchatSalesPageMetricSpecs.length > 0 && (
            <AccordionWithCheckboxes<string>
              title="Snapchat reported metrics"
              isExpandedByDefault={false}
              controlProps={{
                expanded: expandedSections["snapchat-metrics"],
                setExpanded: (expanded) =>
                  setExpandedSection("snapchat-metrics", expanded),
              }}
              search={metricNameSearch}
              options={buildOptionsForMetricSection(
                snapchatSalesPageMetricSpecs,
              )}
              onChange={(id, val) => changeSingleMetric(val, id)}
              onChangeAll={(val) =>
                changeAllMetrics(val, snapchatSalesPageMetricSpecs)
              }
            />
          )}
          {twitterSalesPageMetricSpecs.length > 0 && (
            <AccordionWithCheckboxes<string>
              title="Twitter reported metrics"
              isExpandedByDefault={false}
              controlProps={{
                expanded: expandedSections["twitter-metrics"],
                setExpanded: (expanded) =>
                  setExpandedSection("twitter-metrics", expanded),
              }}
              search={metricNameSearch}
              options={buildOptionsForMetricSection(
                twitterSalesPageMetricSpecs,
              )}
              onChange={(id, val) => changeSingleMetric(val, id)}
              onChangeAll={(val) =>
                changeAllMetrics(val, twitterSalesPageMetricSpecs)
              }
            />
          )}
          {tradedeskSalesPageMetricSpecs.length > 0 && (
            <AccordionWithCheckboxes<string>
              title="Tradedesk reported metrics"
              isExpandedByDefault={false}
              controlProps={{
                expanded: expandedSections["tradedesk-metrics"],
                setExpanded: (expanded) =>
                  setExpandedSection("tradedesk-metrics", expanded),
              }}
              search={metricNameSearch}
              options={buildOptionsForMetricSection(
                tradedeskSalesPageMetricSpecs,
              )}
              onChange={(id, val) => changeSingleMetric(val, id)}
              onChangeAll={(val) =>
                changeAllMetrics(val, tradedeskSalesPageMetricSpecs)
              }
            />
          )}
        </div>
        <div className="border py-5 mx-5 rounded-lg mt-5 ">
          <div className="mx-8 text-xl font-semibold text-primary">
            Attribution Options
          </div>
          <AccordionWithCheckboxes<string>
            title="Attribution model"
            isExpandedByDefault={false}
            options={user.attributionMethodEnum.reactSelectOptions.map(
              ({ value, label }) => {
                const isSelected = selectedAttributionModelIds.has(value);

                return {
                  id: value,
                  label,
                  isSelected,
                  isDisabled:
                    isSelected && selectedAttributionModelIds.size === 1,
                };
              },
            )}
            onChange={(id, val) => {
              val
                ? selectedAttributionModelIds.add(id)
                : selectedAttributionModelIds.delete(id);

              const newSelectedAttributionModelIds = new Set(
                selectedAttributionModelIds,
              );

              prettyLogEventPage({
                domain: "DataExport",
                action: "onSelect",
                additionalActionInfo: "Attribution method changed",
                data: {
                  newSelectedAttributionModelIds,
                  pageContext: { selectedAttributionModelIds },
                },
              });

              setSelectedAttributionModelIds(
                new Set(newSelectedAttributionModelIds),
              );
              updateSelectedMetricsWithAttributionModelIds(
                newSelectedAttributionModelIds,
              );
            }}
            onChangeAll={(val) => {
              for (const { value } of [
                ...user.attributionMethodEnum.reactSelectOptions,
              ].reverse()) {
                if (val) {
                  selectedAttributionModelIds.add(value);
                } else if (selectedAttributionModelIds.size > 1) {
                  selectedAttributionModelIds.delete(value);
                }
              }

              const newSelectedAttributionModelIds = new Set(
                selectedAttributionModelIds,
              );

              prettyLogEventPage({
                domain: "DataExport",
                action: "onSelect",
                additionalActionInfo: "Attribution method all changed",
                data: {
                  newSelectedAttributionModelIds,
                  pageContext: { selectedAttributionModelIds },
                },
              });

              setSelectedAttributionModelIds(
                new Set(newSelectedAttributionModelIds),
              );
              updateSelectedMetricsWithAttributionModelIds(
                newSelectedAttributionModelIds,
              );
            }}
          />
          <AccordionWithCheckboxes<IMCAttributionWindowDays>
            title="Attribution window"
            options={windowOptions.map(({ value, label }) => {
              const isSelected = selectedAttributionWindowIds.has(value);

              return {
                id: value,
                label,
                isSelected,
                isDisabled:
                  isSelected && selectedAttributionWindowIds.size === 1,
              };
            })}
            onChange={(id, val) => {
              val
                ? selectedAttributionWindowIds.add(id)
                : selectedAttributionWindowIds.delete(id);

              const newSelectedAttributionWindowIds = new Set(
                selectedAttributionWindowIds,
              );

              prettyLogEventPage({
                domain: "DataExport",
                action: "onSelect",
                additionalActionInfo: "Attribution window changed",
                data: {
                  newSelectedAttributionWindowIds,
                  pageContext: { selectedAttributionWindowIds },
                },
              });

              setSelectedAttributionWindowIds(newSelectedAttributionWindowIds);
              updateSelectedMetricsWithAttributionWindowIds(
                newSelectedAttributionWindowIds,
              );
            }}
            onChangeAll={(val) => {
              for (const { value } of [...windowOptions].reverse()) {
                if (val) {
                  selectedAttributionWindowIds.add(value);
                } else if (selectedAttributionWindowIds.size > 1) {
                  selectedAttributionWindowIds.delete(value);
                }
              }

              const newSelectedAttributionWindowIds = new Set(
                selectedAttributionWindowIds,
              );

              prettyLogEventPage({
                domain: "DataExport",
                action: "onSelect",
                additionalActionInfo: "Attribution window all changed",
                data: {
                  newSelectedAttributionWindowIds,
                  pageContext: { selectedAttributionWindowIds },
                },
              });

              setSelectedAttributionWindowIds(newSelectedAttributionWindowIds);
              updateSelectedMetricsWithAttributionWindowIds(
                newSelectedAttributionWindowIds,
              );
            }}
          />
          <AccordionWithCheckboxes<INBAccountingMode>
            title="Accounting mode"
            options={NBAccountingMode.reactSelectOptions.map(
              ({ value, label }) => {
                const isSelected = selectedAccountingModeIds.has(value);

                return {
                  id: value,
                  label,
                  isSelected,
                  isDisabled:
                    isSelected && selectedAccountingModeIds.size === 1,
                };
              },
            )}
            onChange={(id, val) => {
              val
                ? selectedAccountingModeIds.add(id)
                : selectedAccountingModeIds.delete(id);

              const newSelectedAccountingModeIds = new Set(
                selectedAccountingModeIds,
              );

              prettyLogEventPage({
                domain: "DataExport",
                action: "onSelect",
                additionalActionInfo: "Accounting mode changed",
                data: {
                  newSelectedAccountingModeIds,
                  pageContext: { selectedAccountingModeIds },
                },
              });

              setSelectedAccountingModeIds(newSelectedAccountingModeIds);
              updateSelectedMetricsWithAccountingModeIds(
                newSelectedAccountingModeIds,
              );
              unselectMetricsForUnselectedAccountingModes(
                newSelectedAccountingModeIds,
              );
            }}
            onChangeAll={(val) => {
              for (const { value } of [
                ...NBAccountingMode.reactSelectOptions,
              ].reverse()) {
                if (val) {
                  selectedAccountingModeIds.add(value);
                } else if (selectedAccountingModeIds.size > 1) {
                  selectedAccountingModeIds.delete(value);
                }
              }

              const newSelectedAccountingModeIds = new Set(
                selectedAccountingModeIds,
              );

              prettyLogEventPage({
                domain: "DataExport",
                action: "onSelect",
                additionalActionInfo: "Accounting mode all changed",
                data: {
                  newSelectedAccountingModeIds,
                  pageContext: { selectedAccountingModeIds },
                },
              });

              setSelectedAccountingModeIds(newSelectedAccountingModeIds);
              updateSelectedMetricsWithAccountingModeIds(
                newSelectedAccountingModeIds,
              );
              unselectMetricsForUnselectedAccountingModes(
                newSelectedAccountingModeIds,
              );
            }}
          />
        </div>
        <div className="border py-5 mx-5 rounded-lg mt-5 ">
          <div className="mx-8 text-xl font-semibold text-primary">
            Export Options
          </div>
          <AccordionWithRadios<IGranularity>
            title="Level"
            options={levelOptions.map((e) => ({
              ...e,
              isSelected: selectedLevel === e.id,
            }))}
            onChange={(val) => {
              prettyLogEventPage({
                domain: "DataExport",
                action: "onSelect",
                additionalActionInfo: "Level changed",
                data: {
                  newLevel: val,
                  pageContext: { level: selectedLevel },
                },
              });

              setSelectedLevel(val);
            }}
          />
          {target === "performance-metrics" && (
            <Accordion title="Breakdown by">
              <p className="text-xs text-zinc-500 font-light m-0">
                Add your Northbeam breakdown values:
              </p>
              <div className="mt-4 pb-[220px] w-full xl:w-3/4">
                <BreakdownSelector
                  menuClassName="max-h-[12rem]"
                  current={selectedBreakdowns}
                  available={breakdownConfigs}
                  allConfigs={breakdownConfigs}
                  tabIndex="0"
                  onUpdate={(values) => {
                    prettyLogEventPage({
                      domain: "DataExport",
                      action: "onSelect",
                      additionalActionInfo: "Breakdown changed",
                      data: {
                        newBreakdowns: values,
                        pageContext: { breakdowns: selectedBreakdowns },
                      },
                    });

                    setSelectedBreakdowns(values);
                  }}
                />
              </div>
            </Accordion>
          )}
          <Accordion title="Additional export options">
            <ExtraOptionsSection
              target={target}
              exportAggregation={exportAggregation}
              removeZeroSpend={removeZeroSpend}
              aggregateData={aggregateData}
              includeIds={includeIds}
              includeKindAndPlatform={includeKindAndPlatform}
              onExportAggregationChange={(val) => setExportAggregation(val)}
              onRemoveZeroSpendChange={(val) => setRemoveZeroSpend(val)}
              onAggregateDataChange={(val) => setAggregateData(val)}
              onIncludeIdsChange={(val) => setIncludeIds(val)}
              onIncludeKindAndPlatformChange={(val) =>
                setIncludeKindAndPlatform(val)
              }
            />
          </Accordion>
        </div>
      </div>
      <SelectedMetrics
        selectedMetrics={selectedMetrics}
        editedMetricId={editedMetricId}
        onChange={(metrics) => setSelectedMetrics(metrics)}
      />
    </div>
  );
}
